import { useSelector, useDispatch } from 'react-redux';
import {
  createSavedListDetails,
  addProductToSavedList,
  deleteSavedListDetails
} from '@app/store/account/actions';
import {
  AFFILIATION_DEALER,
  PUBLIC_LIST,
  DEALER_LIST
} from '@app/constants/commonConstants';
import * as XLSX from 'xlsx';
import { useAnalytics, useFitmentCheck } from '@app/hooks';
import { getPartName } from '@app/utils/commonUtils';

const useSavedListUploader = ({ defaultRowValues }) => {
  const dispatch = useDispatch();
  const userAffiliation = useSelector(s => s.common?.userAffiliation);
  const store = useSelector(s => s.store) ?? '';
  const dealerStore = useSelector(s => s.common?.storeLocationName) || '';
  const model = useSelector(
    s => s.myEquipment?.equipments?.selectedEquipment?.model
  );
  const { getFitmentCheck } = useFitmentCheck();
  const isDealerUser = userAffiliation === AFFILIATION_DEALER;
  const statusType = isDealerUser ? DEALER_LIST : PUBLIC_LIST;
  const getOtherStores = store?.otherStores?.dealerStores.map(
    store => store.name
  );
  const { fireAddToWishlistEvent } = useAnalytics();
  const addToWishListGAEventSuccess = (allItems, listName) => {
    const itemsArray = Array.isArray(allItems) ? allItems : [allItems];
    const itemsDetails = itemsArray?.map((product, index) => ({
      partNumber: product?.partNumber || '',
      name: getPartName(product?.partNumber, product?.name) || '',
      businessUnit: 'PCC',
      coupon: '',
      locationId: getOtherStores?.join('|') || dealerStore,
      brand: product?.manufacturer || 'Cat',
      quantity: product?.quantity,
      position: index + 1,
      checkFitment: getFitmentCheck(product?.partNumber) || '',
      relatedModel: model
    }));
    return fireAddToWishlistEvent(itemsDetails, listName);
  };
  const handleFileData = (file, listName, onCreateListSuccess) => {
    let allItems = [];
    try {
      const reader = new FileReader();
      reader.onloadend = () => {
        const workbook = XLSX.read(reader.result, {
          type: 'binary'
        });
        workbook.SheetNames.forEach(sheetName => {
          const csv = XLSX.utils.sheet_to_csv(workbook.Sheets[sheetName]);
          const parsedValuesArray = csv
            ?.replace(/(^\s+)|(\s+$)/g, '')
            ?.split('\n');

          const entries = parsedValuesArray.slice(1);
          const columnKeys = Object.keys(defaultRowValues);
          let validRows = 0;
          const newItems = entries.reduce((acc, item) => {
            // split on , or tab
            const list = item
              .replace(/^\t/, ' \t') // leading tab
              .replace(/\t\t/, '\t \t')
              .replace(/,,/, ', ,')
              .replace(/^,/, ' ,') // leading comma
              .match(/(?:[^,\t"]+|"[^"]*")+/g);
            if (
              list[0] &&
              list[1] &&
              !isNaN(list[0]) &&
              !isNaN(parseFloat(list[0])) // whitespace string
            ) {
              const row = validRows;
              validRows++;
              const newValues = columnKeys.reduce((acc, key, i) => {
                // remove leading and trailing " or spaces
                const v = list[i]?.replace(/^"|^"|"$/g, '').trim() ?? '';
                return { ...acc, [key]: v };
              }, {});
              if (acc[row]) {
                acc[row] = { ...acc[row], ...newValues };
              } else {
                acc[row] = newValues;
              }
            }
            return acc;
          }, []);
          if (validRows) {
            allItems = [...allItems, ...newItems];
          }
        });

        allItems = allItems
          .map(
            ({
              assetId,
              customerItemNumber,
              customerPartNumber,
              itemNumber,
              lineItemNote,
              quantity
            }) => ({
              partNumber: itemNumber,
              quantity,
              serialNumber: assetId,
              lineItemNotes: lineItemNote,
              customerPartNumber,
              customerItemNumber
            })
          )
          .reverse();
        const onSuccess = res => {
          if (res) {
            if (allItems.length > 0) {
              const requisitionListId = res.requisitionListId;
              dispatch(
                addProductToSavedList({
                  status: statusType,
                  requisitionListId,
                  nameData: listName,
                  saveListData: allItems,
                  close: onCreateListSuccess,
                  successCb: addToWishListGAEventSuccess(allItems, listName)
                })
              ).then(val => {
                if (!val) {
                  dispatch(deleteSavedListDetails(requisitionListId));
                }
              });
            } else {
              onCreateListSuccess && onCreateListSuccess();
            }
          }
        };
        dispatch(
          createSavedListDetails(
            listName,
            statusType,
            null,
            null,
            null,
            onSuccess
          )
        );
      };
      reader.readAsArrayBuffer(file);
    } catch (e) {
      console.log(e);
    }
  };

  const handleFileUpload = (fileObj, listName, onCreateListSuccess) => {
    handleFileData(fileObj, listName, onCreateListSuccess);
  };
  return { handleFileUpload };
};

export default useSavedListUploader;
